import Quill from 'quill'

const Delta = Quill.import('delta')
const icons = Quill.import('ui/icons')
const Keyboard = Quill.import('modules/keyboard')

const MAX_TABLE_ROWS = 10
const MAX_TABLE_COLUMNS = 6
const FAQ_TABLE_PLACEHOLDER = '\u00a0'
const FAQ_TABLE_BLOCK_MESSAGE = "Les tableaux ne sont pas disponibles à l'intérieur d'un accordéon."

icons.accordion = '<svg viewBox="0 0 18 18"><polyline class="ql-stroke" points="4 6 9 11 14 6"></polyline></svg>'
icons.table = '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="12" width="12" x="3" y="3"></rect><line class="ql-stroke" x1="3" x2="15" y1="7" y2="7"></line><line class="ql-stroke" x1="3" x2="15" y1="11" y2="11"></line><line class="ql-stroke" x1="7" x2="7" y1="3" y2="15"></line><line class="ql-stroke" x1="11" x2="11" y1="3" y2="15"></line></svg>'
icons['table-row'] = '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="10" width="12" x="3" y="4"></rect><line class="ql-stroke" x1="3" x2="15" y1="9" y2="9"></line><line class="ql-stroke" x1="6" x2="12" y1="15" y2="15"></line><line class="ql-stroke" x1="9" x2="9" y1="13" y2="17"></line></svg>'
icons['table-column'] = '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="12" width="10" x="4" y="3"></rect><line class="ql-stroke" x1="9" x2="9" y1="3" y2="15"></line><line class="ql-stroke" x1="15" x2="15" y1="6" y2="12"></line><line class="ql-stroke" x1="13" x2="17" y1="9" y2="9"></line></svg>'
icons['table-row-remove'] = '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="10" width="12" x="3" y="4"></rect><line class="ql-stroke" x1="3" x2="15" y1="9" y2="9"></line><line class="ql-stroke" x1="6" x2="12" y1="15" y2="15"></line><line class="ql-stroke" x1="6" x2="12" y1="2" y2="2"></line></svg>'
icons['table-column-remove'] = '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="12" width="10" x="4" y="3"></rect><line class="ql-stroke" x1="9" x2="9" y1="3" y2="15"></line><line class="ql-stroke" x1="15" x2="15" y1="6" y2="12"></line><line class="ql-stroke" x1="13" x2="17" y1="9" y2="9"></line><line class="ql-stroke" x1="1" x2="17" y1="16" y2="16"></line></svg>'

const buildFaqQuillOptions = (vm) => ({
  placeholder: 'Rédigez votre réponse ici...',
  modules: {
    toolbar: {
      container: [
        ['bold', 'italic'],
        [{ list: 'bullet' }],
        ['link', 'accordion', 'table', 'table-row', 'table-column', 'table-row-remove', 'table-column-remove']
      ],
      handlers: {
        accordion () {
          vm.insertFaqAccordion(this.quill)
        },
        table () {
          vm.promptAndInsertTable(this.quill)
        },
        'table-row' () {
          vm.addFaqTableRow(this.quill)
        },
        'table-column' () {
          vm.addFaqTableColumn(this.quill)
        },
        'table-row-remove' () {
          vm.removeFaqTableRow(this.quill)
        },
        'table-column-remove' () {
          vm.removeFaqTableColumn(this.quill)
        }
      }
    }
  },
  formats: [
    'bold',
    'italic',
    'list',
    'link',
    'image',
    'video',
    'accordion-title',
    'accordion-content',
    'faq-table-row',
    'faq-table-cell'
  ]
})

export default {
  data () {
    return {
      quillOptions: buildFaqQuillOptions(this),
      faqTableCleanup: null
    }
  },
  mounted () {
    this.decorateQuillButtons()
    this.activateAccordionPreview()
    this.setupFaqTableGuards()
  },
  updated () {
    this.decorateQuillButtons()
    this.activateAccordionPreview()
    this.setupFaqTableGuards()
  },
  beforeDestroy () {
    if (this.faqTableCleanup) {
      this.faqTableCleanup()
      this.faqTableCleanup = null
    }
  },
  methods: {
    decorateQuillButtons () {
      this.$nextTick(() => {
        if (!this.$el) return
        const toolbar = this.$el.querySelector('.ql-toolbar')
        if (!toolbar) return
        this.applyToolbarLabels(toolbar.querySelectorAll('button.ql-accordion'), 'Ajouter un accordéon')
        this.applyToolbarLabels(toolbar.querySelectorAll('button.ql-table'), 'Insérer un tableau')
        this.applyToolbarLabels(toolbar.querySelectorAll('button.ql-table-row'), 'Ajouter une ligne')
        this.applyToolbarLabels(toolbar.querySelectorAll('button.ql-table-column'), 'Ajouter une colonne')
        this.applyToolbarLabels(toolbar.querySelectorAll('button.ql-table-row-remove'), 'Supprimer une ligne')
        this.applyToolbarLabels(toolbar.querySelectorAll('button.ql-table-column-remove'), 'Supprimer une colonne')
      })
    },
    applyToolbarLabels (buttons, label) {
      if (!buttons || !buttons.length) return
      buttons.forEach(button => {
        if (!button.getAttribute('title')) {
          button.setAttribute('title', label)
          button.setAttribute('aria-label', label)
        }
      })
    },
    insertFaqAccordion (quillInstance) {
      const range = quillInstance.getSelection(true) || { index: quillInstance.getLength(), length: 0 }
      const currentLeaf = quillInstance.getLeaf(range.index)
      if (currentLeaf && currentLeaf[0] && currentLeaf[0].parent && currentLeaf[0].parent.domNode) {
        const parentNode = currentLeaf[0].parent.domNode
        if (parentNode.closest && parentNode.closest('.faq-accordion__title, .faq-accordion__content')) {
          // already inside an accordion; do not insert another
          return
        }
      }
      const delta = new Delta()
        .retain(range.index)
        .delete(range.length)
        .insert("Titre de l'accordéon")
        .insert('\n', { 'accordion-title': true })
        .insert('Contenu de votre accordéon (cliquez ici pour modifier le texte).')
        .insert('\n', { 'accordion-content': true })
        .insert('\n')
      quillInstance.updateContents(delta, 'user')
      quillInstance.setSelection(range.index + delta.length(), 0)
      this.activateAccordionPreview()
    },
    insertAccordionByRef (refName) {
      if (!refName || !this.$refs) return
      const target = this.$refs[refName]
      if (!target) return
      const editorComponent = Array.isArray(target) ? target[0] : target
      const quillInstance = editorComponent && editorComponent.quill
      if (quillInstance) {
        this.insertFaqAccordion(quillInstance)
        this.decorateQuillButtons()
      }
    },
    async promptAndInsertTable (quillInstance) {
      if (!quillInstance) return
      const range = quillInstance.getSelection(true) || { index: quillInstance.getLength(), length: 0 }
      if (this.isTableActionBlockedInAccordion(quillInstance, range)) return
      const rows = await this.requestTableDimension('Combien de lignes doit contenir votre tableau ?', 2, MAX_TABLE_ROWS)
      if (!rows) return
      const columns = await this.requestTableDimension('Combien de colonnes doit contenir votre tableau ?', 2, MAX_TABLE_COLUMNS)
      if (!columns) return
      this.insertFaqTable(quillInstance, rows, columns, range)
    },
    requestTableDimension (message, defaultValue, maxValue) {
      return new Promise(resolve => {
        if (!this.$buefy || !this.$buefy.dialog) {
          resolve(null)
          return
        }
        this.$buefy.dialog.prompt({
          message,
          confirmText: 'Valider',
          cancelText: 'Annuler',
          inputAttrs: {
            type: 'number',
            min: 1,
            max: maxValue,
            value: defaultValue
          },
          onConfirm: value => {
            const parsedValue = parseInt(value, 10)
            if (Number.isNaN(parsedValue) || parsedValue < 1) {
              this.notifyInvalidTableInput()
              resolve(null)
              return
            }
            resolve(Math.min(parsedValue, maxValue))
          },
          onCancel: () => resolve(null)
        })
      })
    },
    notifyInvalidTableInput () {
      if (!this.$buefy || !this.$buefy.toast) return
      this.$buefy.toast.open({
        message: 'Veuillez saisir un nombre supérieur à 0.',
        type: 'is-danger',
        position: 'is-bottom-left',
        duration: 4000
      })
    },
    notifyFaqTableActionError (message) {
      if (!this.$buefy || !this.$buefy.toast) return
      this.$buefy.toast.open({
        message,
        type: 'is-warning',
        position: 'is-bottom-left',
        duration: 4000
      })
    },
    insertFaqTable (quillInstance, rows, columns, rangeOverride = null) {
      const safeRows = this.sanitizeTableDimension(rows, MAX_TABLE_ROWS)
      const safeColumns = this.sanitizeTableDimension(columns, MAX_TABLE_COLUMNS)
      const blockFormats = this.extractBlockFormats(quillInstance, (quillInstance.getSelection(true) || { index: quillInstance.getLength() }).index)
      const tableDelta = this.buildFaqTableDelta(safeRows, safeColumns, blockFormats)
      const range = rangeOverride || quillInstance.getSelection(true) || { index: quillInstance.getLength(), length: 0 }
      if (this.isTableActionBlockedInAccordion(quillInstance, range)) return
      const changeDelta = new Delta()
        .retain(range.index)
        .delete(range.length)
        .concat(tableDelta)
      quillInstance.updateContents(changeDelta, 'user')
      quillInstance.setSelection(range.index + tableDelta.length(), 0)
    },
    addFaqTableRow (quillInstance) {
      if (!quillInstance) return
      const range = quillInstance.getSelection(true)
      if (!range) {
        this.notifyFaqTableActionError('Sélectionnez une cellule du tableau pour ajouter une ligne.')
        return
      }
      if (this.isTableActionBlockedInAccordion(quillInstance, range)) return
      this.logFaqTableSelection(quillInstance, range, 'add-row:before-resolution')
      const rowInfo = this.resolveFaqTableRowInfo(quillInstance, range.index)
      if (!rowInfo) {
        this.logFaqTableSelection(quillInstance, range, 'add-row:no-row-info')
        this.notifyFaqTableActionError("Placez votre curseur à l'intérieur d'un tableau pour ajouter une ligne.")
        return
      }
      const tableRows = this.collectFaqTableRows(rowInfo.node)
      if (tableRows.length >= MAX_TABLE_ROWS) {
        this.notifyFaqTableActionError(`Un tableau ne peut pas contenir plus de ${MAX_TABLE_ROWS} lignes.`)
        return
      }
      const columns = this.getColumnsFromRow(rowInfo.node)
      const rowDelta = this.buildFaqTableRowInsertionDelta(columns, rowInfo.blot.formats())
      const insertIndex = rowInfo.startIndex + rowInfo.blot.length()
      const changeDelta = new Delta().retain(insertIndex).concat(rowDelta)
      quillInstance.updateContents(changeDelta, 'user')
      quillInstance.setSelection(insertIndex, 0, 'silent')
      this.normalizeFaqTablePositions(quillInstance.root, quillInstance)
    },
    removeFaqTableRow (quillInstance) {
      if (!quillInstance) return
      const range = quillInstance.getSelection(true)
      if (!range) {
        this.notifyFaqTableActionError('Sélectionnez une cellule du tableau pour supprimer une ligne.')
        return
      }
      if (this.isTableActionBlockedInAccordion(quillInstance, range)) return
      const rowInfo = this.resolveFaqTableRowInfo(quillInstance, range.index)
      if (!rowInfo) {
        this.notifyFaqTableActionError("Placez votre curseur à l'intérieur d'un tableau pour supprimer une ligne.")
        return
      }
      const tableRows = this.collectFaqTableRows(rowInfo.node)
      const rowIndex = tableRows.indexOf(rowInfo.node)
      const fallbackRowNode = tableRows[rowIndex + 1] || tableRows[rowIndex - 1] || null
      quillInstance.deleteText(rowInfo.startIndex, rowInfo.blot.length(), 'user')
      this.normalizeFaqTablePositions(quillInstance.root, quillInstance)
      this.ensureFaqTableStructure(quillInstance.root)
      if (fallbackRowNode) {
        const fallbackInfo = this.getRowInfoFromNode(quillInstance, fallbackRowNode)
        if (fallbackInfo) {
          quillInstance.setSelection(fallbackInfo.startIndex, 0, 'silent')
          return
        }
      }
      const fallbackIndex = Math.max(Math.min(rowInfo.startIndex, quillInstance.getLength() - 1), 0)
      quillInstance.setSelection(fallbackIndex, 0, 'silent')
    },
    addFaqTableColumn (quillInstance) {
      if (!quillInstance) return
      const range = quillInstance.getSelection(true)
      if (!range) {
        this.notifyFaqTableActionError('Sélectionnez une cellule du tableau pour ajouter une colonne.')
        return
      }
      if (this.isTableActionBlockedInAccordion(quillInstance, range)) return
      this.logFaqTableSelection(quillInstance, range, 'add-column:before-resolution')
      const rowInfo = this.resolveFaqTableRowInfo(quillInstance, range.index)
      if (!rowInfo) {
        this.logFaqTableSelection(quillInstance, range, 'add-column:no-row-info')
        this.notifyFaqTableActionError("Placez votre curseur à l'intérieur d'un tableau pour ajouter une colonne.")
        return
      }
      const baseColumns = this.getColumnsFromRow(rowInfo.node)
      if (baseColumns >= MAX_TABLE_COLUMNS) {
        this.notifyFaqTableActionError(`Un tableau ne peut pas contenir plus de ${MAX_TABLE_COLUMNS} colonnes.`)
        return
      }
      const tableRows = this.collectFaqTableRows(rowInfo.node)
      const newColumnIdentifier = this.buildTableCellIdentifier(baseColumns)
      tableRows.forEach(rowNode => {
        const info = this.getRowInfoFromNode(quillInstance, rowNode)
        if (!info) return
        const insertIndex = Math.max(info.startIndex + info.blot.length() - 1, info.startIndex)
        quillInstance.insertText(insertIndex, FAQ_TABLE_PLACEHOLDER, { 'faq-table-cell': newColumnIdentifier }, 'user')
        info.blot.format('faq-table-row', {
          columns: baseColumns + 1,
          position: rowNode.getAttribute('data-table-position') || 'middle'
        })
      })
      quillInstance.setSelection(range.index, 0, 'silent')
      this.ensureFaqTableStructure(quillInstance.root)
    },
    removeFaqTableColumn (quillInstance) {
      if (!quillInstance) return
      const range = quillInstance.getSelection(true)
      if (!range) {
        this.notifyFaqTableActionError('Sélectionnez une cellule du tableau pour supprimer une colonne.')
        return
      }
      if (this.isTableActionBlockedInAccordion(quillInstance, range)) return
      let cellInfo = this.resolveFaqTableCellInfo(quillInstance, range.index)
      if (!cellInfo) {
        const rowInfo = this.resolveFaqTableRowInfo(quillInstance, range.index)
        if (rowInfo) {
          cellInfo = this.findNearestCellInfoInRow(quillInstance, rowInfo.node, range.index)
        }
      }
      if (!cellInfo || !cellInfo.node) {
        this.notifyFaqTableActionError("Placez votre curseur à l'intérieur d'un tableau pour supprimer une colonne.")
        return
      }
      const rowNode = this.findFaqTableRowNode(cellInfo.node)
      if (!rowNode) {
        this.notifyFaqTableActionError("Impossible d'identifier la colonne à supprimer.")
        return
      }
      const columnNumber = this.extractColumnNumber(cellInfo.node.getAttribute('data-table-cell'))
      if (Number.isNaN(columnNumber) || columnNumber < 1) {
        this.notifyFaqTableActionError("Impossible d'identifier la colonne à supprimer.")
        return
      }
      const baseColumns = this.getColumnsFromRow(rowNode)
      if (baseColumns <= 1) {
        this.notifyFaqTableActionError('Un tableau doit contenir au moins une colonne.')
        return
      }
      const targetIdentifier = this.buildTableCellIdentifier(columnNumber - 1)
      const tableRows = this.collectFaqTableRows(rowNode)
      const deleteTargets = []
      tableRows.forEach(row => {
        const cell = row.querySelector(`.faq-table__cell[data-table-cell="${targetIdentifier}"]`)
        if (!cell) return
        const info = this.getCellInfoFromNode(quillInstance, cell)
        if (info) {
          deleteTargets.push(info)
        }
      })
      if (!deleteTargets.length) {
        this.notifyFaqTableActionError("Impossible d'identifier la colonne à supprimer.")
        return
      }
      deleteTargets.sort((a, b) => b.startIndex - a.startIndex)
      deleteTargets.forEach(target => {
        if (target.blot && typeof target.blot.remove === 'function') {
          try {
            target.blot.remove()
            return
          } catch (error) {
            console.warn('[FAQ table] failed blot removal, falling back to deleteText', error)
          }
        }
        quillInstance.deleteText(target.startIndex, target.blot ? target.blot.length() : 1, 'user')
      })
      this.reindexFaqTableColumns(tableRows, quillInstance)
      this.normalizeFaqTablePositions(quillInstance.root, quillInstance)
      this.ensureFaqTableStructure(quillInstance.root)
      const fallbackIdentifier = this.buildTableCellIdentifier(Math.max(columnNumber - 2, 0))
      const fallbackCell = rowNode.querySelector(`.faq-table__cell[data-table-cell="${fallbackIdentifier}"]`) || rowNode.querySelector('.faq-table__cell')
      const fallbackInfo = fallbackCell ? this.getCellInfoFromNode(quillInstance, fallbackCell) : null
      if (fallbackInfo) {
        quillInstance.setSelection(fallbackInfo.startIndex, 0, 'silent')
      } else {
        const fallbackIndex = Math.max(Math.min(range.index, quillInstance.getLength() - 1), 0)
        quillInstance.setSelection(fallbackIndex, 0, 'silent')
      }
    },
    sanitizeTableDimension (value, maxValue) {
      const parsed = parseInt(value, 10)
      if (Number.isNaN(parsed) || parsed < 1) return 1
      return Math.min(parsed, maxValue)
    },
    extractBlockFormats (quillInstance, index) {
      if (!quillInstance || typeof index !== 'number') return {}
      const formats = quillInstance.getFormat(index)
      return this.extractAccordionContentFormat(formats)
    },
    extractAccordionContentFormat (formats = {}) {
      const result = {}
      if (formats['faq-table-row']) {
        result['faq-table-row'] = formats['faq-table-row']
      }
      return result
    },
    buildFaqTableDelta (rows, columns, blockFormats = {}) {
      const delta = new Delta()
      for (let rowIndex = 0; rowIndex < rows; rowIndex += 1) {
        for (let columnIndex = 0; columnIndex < columns; columnIndex += 1) {
          delta.insert(`Cellule ${rowIndex + 1}-${columnIndex + 1}`, { 'faq-table-cell': this.buildTableCellIdentifier(columnIndex) })
        }
        delta.insert('\n', {
          'faq-table-row': {
            columns,
            position: this.resolveTableRowPosition(rowIndex, rows)
          },
          ...blockFormats
        })
      }
      if (blockFormats && Object.keys(blockFormats).length > 0) {
        delta.insert('\n', blockFormats)
      } else {
        delta.insert('\n')
      }
      return delta
    },
    buildFaqTableRowInsertionDelta (columns, baseFormats = {}) {
      const safeColumns = this.sanitizeTableDimension(columns, MAX_TABLE_COLUMNS)
      const delta = new Delta()
      for (let columnIndex = 0; columnIndex < safeColumns; columnIndex += 1) {
        delta.insert(FAQ_TABLE_PLACEHOLDER, { 'faq-table-cell': this.buildTableCellIdentifier(columnIndex) })
      }
      delta.insert('\n', {
        'faq-table-row': {
          columns: safeColumns,
          position: 'middle'
        },
        ...this.extractAccordionContentFormat(baseFormats)
      })
      return delta
    },
    buildTableCellIdentifier (columnIndex) {
      return `col-${columnIndex + 1}`
    },
    resolveTableRowPosition (rowIndex, totalRows) {
      if (totalRows === 1) return 'single'
      if (rowIndex === 0) return 'first'
      if (rowIndex === totalRows - 1) return 'last'
      return 'middle'
    },
    activateAccordionPreview () {
      this.$nextTick(() => {
        if (!this.$el) return
        const editor = this.$el.querySelector('.ql-editor')
        if (!editor) return
        editor.querySelectorAll('.faq-accordion__title').forEach(titleEl => {
          if (!titleEl.innerHTML || !titleEl.innerHTML.trim() || titleEl.innerHTML === '<br>') {
            titleEl.innerHTML = '&nbsp;'
          }
          titleEl.classList.add('is-open')
          titleEl.style.display = 'block'
        })
        editor.querySelectorAll('.faq-accordion__content').forEach(contentEl => {
          contentEl.classList.add('is-open')
          contentEl.style.display = 'block'
        })
      })
    },
    setupFaqTableGuards () {
      this.$nextTick(() => {
        const quill = this.findQuillInstance()
        if (!quill) return
        if (!quill.__faqTableGuarded) {
          this.registerFaqTableKeyboardBindings(quill)
          quill.__faqTableGuarded = true
        }
        if (!this.faqTableCleanup) {
          const handler = () => {
            this.ensureFaqTableStructure(quill.root)
            quill.update('silent')
          }
          const keydownHandler = (event) => {
            if (!event || event.key !== 'Enter') return
            const range = quill.getSelection(true)
            if (!range) return
            const handled = this.handleFaqTableEnter(quill, range)
            if (handled) {
              event.preventDefault()
              event.stopPropagation()
            }
          }
          const selectionHandler = (range, oldRange, source) => {
            if (!range || range.length) return
            const cellInfo = this.resolveFaqTableCellInfo(quill, range.index)
            const rowInfo = this.resolveFaqTableRowInfo(quill, range.index)
            let columnIndex = this.getColumnIndexFromCell(rowInfo && rowInfo.node, cellInfo && cellInfo.node)
            if (columnIndex === null) {
              columnIndex = this.getColumnIndexFromNativeSelection(quill)
            }
            if (!Number.isInteger(columnIndex)) {
              quill.__faqEnterLockedColumnIndex = null
              return
            }
            if (quill.__faqEnterLockedColumnIndex === null || source === Quill.sources.USER) {
              quill.__faqEnterLockedColumnIndex = columnIndex
            }
          }
          quill.on('text-change', handler)
          quill.root.addEventListener('keydown', keydownHandler, true)
          quill.on('selection-change', selectionHandler)
          this.faqTableCleanup = () => {
            quill.off('text-change', handler)
            quill.root.removeEventListener('keydown', keydownHandler, true)
            quill.off('selection-change', selectionHandler)
          }
        }
        this.ensureFaqTableStructure(quill.root)
      })
    },
    findQuillInstance () {
      if (this.$refs) {
        for (const refName of Object.keys(this.$refs)) {
          const ref = this.$refs[refName]
          const component = Array.isArray(ref) ? ref[0] : ref
          if (component && component.quill) {
            return component.quill
          }
        }
      }
      if (!this.$el) return null
      const editor = this.$el.querySelector('.ql-editor')
      if (editor && editor.__quill) {
        return editor.__quill
      }
      return null
    },
    registerFaqTableKeyboardBindings (quill) {
      const collapsedHandler = this.buildCollapsedDeletionGuard(quill)
      quill.keyboard.addBinding({ key: Keyboard.keys.BACKSPACE }, {}, collapsedHandler)
      quill.keyboard.addBinding({ key: Keyboard.keys.DELETE }, {}, collapsedHandler)
      const selectionHandler = this.buildSelectionDeletionGuard(quill)
      quill.keyboard.addBinding({ key: Keyboard.keys.BACKSPACE }, { collapsed: false }, selectionHandler)
      quill.keyboard.addBinding({ key: Keyboard.keys.DELETE }, { collapsed: false }, selectionHandler)
      const tableEnterHandler = this.buildTableRowEnterBinding(quill)
      quill.keyboard.addBinding({ key: Keyboard.keys.ENTER }, { collapsed: true }, tableEnterHandler)
      const exitAccordionHandler = this.buildAccordionContentExitBinding(quill)
      quill.keyboard.addBinding({ key: Keyboard.keys.ENTER }, { collapsed: true }, exitAccordionHandler)
    },
    buildCollapsedDeletionGuard (quill) {
      return (range, context) => {
        if (!range || range.length !== 0) return true
        const cellInfo = this.resolveFaqTableCellInfo(quill, range.index)
        if (!cellInfo) return true
        const cellLength = this.getCellTextLength(cellInfo.node)
        if (cellLength > 1) return true
        this.setCellPlaceholder(cellInfo.node)
        quill.setSelection(cellInfo.startIndex, 0, 'silent')
        return false
      }
    },
    buildSelectionDeletionGuard (quill) {
      return (range, context) => {
        if (!range || range.length <= 0) return true
        const startCell = this.resolveFaqTableCellInfo(quill, range.index)
        const endCell = this.resolveFaqTableCellInfo(quill, range.index + range.length - 1)
        if (!startCell || !endCell || startCell.node !== endCell.node) return true
        const cellLength = startCell.blot.length()
        const selectionStartOffset = range.index - startCell.startIndex
        const selectionEndOffset = selectionStartOffset + range.length
        if (selectionStartOffset > 0 || selectionEndOffset < cellLength) return true
        this.setCellPlaceholder(startCell.node)
        quill.setSelection(startCell.startIndex, 0, 'silent')
        return false
      }
    },
    buildTableRowEnterBinding (quill) {
      return (range, context) => {
        return !this.handleFaqTableEnter(quill, range)
      }
    },
    handleFaqTableEnter (quill, range) {
      if (!quill || !range || range.length) return false
      let cellInfo = this.resolveFaqTableCellInfo(quill, range.index)
      let rowInfo = this.resolveFaqTableRowInfo(quill, range.index)
      if (!rowInfo && cellInfo && cellInfo.node) {
        const rowNode = this.findFaqTableRowNode(cellInfo.node)
        rowInfo = this.getRowInfoFromNode(quill, rowNode)
      }
      if (!rowInfo) return false
      if (!cellInfo && rowInfo.node) {
        cellInfo = this.findNearestCellInfoInRow(quill, rowInfo.node, range.index)
      }
      const tableRows = this.collectFaqTableRows(rowInfo.node)
      if (!tableRows.length) return false
      const rowIndex = tableRows.indexOf(rowInfo.node)
      if (rowIndex === -1) return false
      const currentIdentifier = cellInfo && cellInfo.node ? cellInfo.node.getAttribute('data-table-cell') : null
      const parsedFromId = currentIdentifier ? this.extractColumnNumber(currentIdentifier) - 1 : NaN
      let currentColumnIndex = !Number.isNaN(parsedFromId) && parsedFromId >= 0 ? parsedFromId : null
      if (currentColumnIndex === null) {
        currentColumnIndex = this.getColumnIndexFromNativeSelection(quill)
      }
      const lockedColumnIndex = Number.isInteger(quill.__faqEnterLockedColumnIndex) ? quill.__faqEnterLockedColumnIndex : null
      const targetColumnIndex = lockedColumnIndex !== null ? lockedColumnIndex : (currentColumnIndex !== null ? currentColumnIndex : 0)
      const preferredIdentifier = this.buildTableCellIdentifier(targetColumnIndex)
      if (lockedColumnIndex === null && currentColumnIndex !== null) {
        quill.__faqEnterLockedColumnIndex = currentColumnIndex
      }
      try {
        console.log('[FAQ table] Enter nav', {
          selection: { index: range.index, length: range.length },
          rowIndex,
          totalRows: tableRows.length,
          columnIndex: currentColumnIndex,
          lockedColumnIndex: quill.__faqEnterLockedColumnIndex,
          preferredColumnIndex: targetColumnIndex,
          currentIdentifier,
          preferredIdentifier
        })
      } catch (_) {}
      if (rowIndex >= 0 && rowIndex < tableRows.length - 1) {
        const targetRow = tableRows[rowIndex + 1]
        const targetIdentifier = preferredIdentifier
        let targetCell = targetIdentifier ? targetRow.querySelector(`.faq-table__cell[data-table-cell="${targetIdentifier}"]`) : null
        if (!targetCell) {
          const cells = Array.from(targetRow.querySelectorAll('.faq-table__cell'))
          targetCell = cells[targetColumnIndex] || cells[0] || null
        }
        const targetInfo = targetCell ? this.getCellInfoFromNode(quill, targetCell) : this.findNearestCellInfoInRow(quill, targetRow, range.index)
        if (targetInfo) {
          try {
            console.log('[FAQ table] Enter target', {
              targetIndex: targetInfo.startIndex,
              targetIdentifier: targetCell && targetCell.getAttribute('data-table-cell')
            })
          } catch (_) {}
          quill.__faqEnterLockedColumnIndex = targetColumnIndex
          const targetLength = targetInfo.blot ? targetInfo.blot.length() : 1
          const cursorIndex = Math.min(targetInfo.startIndex + Math.max(targetLength, 1), quill.getLength())
          quill.setSelection(cursorIndex, 0, 'silent')
          return true
        }
      }
      const exitIndex = rowInfo.startIndex + rowInfo.blot.length()
      const safeIndex = Math.min(exitIndex, quill.getLength())
      quill.setSelection(safeIndex, 0, 'silent')
      quill.__faqEnterLockedColumnIndex = null
      return true
    },
    buildAccordionContentExitBinding (quill) {
      return (range, context) => {
        if (!range || range.length) return true
        const [line, offset] = quill.getLine(range.index)
        if (!line || typeof line.length !== 'function' || typeof line.formats !== 'function') {
          return true
        }
        const formats = line.formats() || {}
        if (!formats['accordion-content']) return true
        const lineLength = Math.max(line.length() - 1, 0)
        if (offset < lineLength) return true
        quill.insertText(range.index, '\n', Quill.sources.USER)
        const newIndex = range.index + 1
        quill.formatLine(newIndex, 1, 'accordion-content', false)
        quill.setSelection(newIndex, 0, 'silent')
        this.ensureAccordionWrappers()
        return false
      }
    },
    resolveFaqTableRowInfo (quill, index) {
      if (!quill || typeof index !== 'number') return null
      const primary = this.findRowInfoAtIndex(quill, Math.max(index, 0))
      if (primary) return primary
      if (index > 0) {
        return this.findRowInfoAtIndex(quill, index - 1)
      }
      return null
    },
    resolveFaqTableCellInfo (quill, index) {
      if (!quill || typeof index !== 'number') return null
      const primary = this.findCellInfoAtIndex(quill, Math.max(index, 0))
      if (primary) return primary
      if (index > 0) {
        return this.findCellInfoAtIndex(quill, index - 1)
      }
      return null
    },
    findCellInfoAtIndex (quill, index) {
      if (index < 0) return null
      const [leaf] = quill.getLeaf(index)
      if (!leaf) return null
      const cellNode = this.findFaqTableCellNode(leaf.domNode)
      if (!cellNode) return null
      const blot = Quill.find(cellNode)
      if (!blot) return null
      const startIndex = blot.offset(quill.scroll)
      return { node: cellNode, blot, startIndex }
    },
    findRowInfoAtIndex (quill, index) {
      if (index < 0) return null
      const [line] = quill.getLine(index)
      if (!line || !line.domNode) return null
      const rowNode = this.findFaqTableRowNode(line.domNode)
      if (!rowNode) return null
      const blot = Quill.find(rowNode)
      if (!blot) return null
      const startIndex = blot.offset(quill.scroll)
      return { node: rowNode, blot, startIndex }
    },
    getRowInfoFromNode (quill, rowNode) {
      if (!quill || !rowNode) return null
      const blot = Quill.find(rowNode)
      if (!blot) return null
      const startIndex = blot.offset(quill.scroll)
      return { node: rowNode, blot, startIndex }
    },
    getCellInfoFromNode (quill, cellNode) {
      if (!quill || !cellNode) return null
      const blot = Quill.find(cellNode)
      if (!blot) return null
      const startIndex = blot.offset(quill.scroll)
      return { node: cellNode, blot, startIndex }
    },
    getCellColumnIndex (rowNode, cellNode) {
      if (!rowNode || !cellNode) return 0
      const cells = Array.from(rowNode.querySelectorAll('.faq-table__cell'))
      const index = cells.indexOf(cellNode)
      return index >= 0 ? index : 0
    },
    getColumnIndexFromCell (rowNode, cellNode) {
      if (!rowNode || !cellNode) return null
      const fromId = this.extractColumnNumber(cellNode.getAttribute('data-table-cell')) - 1
      if (!Number.isNaN(fromId) && fromId >= 0) return fromId
      const idx = this.getCellColumnIndex(rowNode, cellNode)
      return Number.isInteger(idx) ? idx : null
    },
    getColumnIndexFromNativeSelection (quill) {
      if (!quill || !quill.root || typeof document === 'undefined') return null
      const sel = document.getSelection()
      if (!sel || !sel.anchorNode) return null
      const cellNode = this.findFaqTableCellNode(sel.anchorNode)
      const rowNode = this.findFaqTableRowNode(sel.anchorNode)
      if (!cellNode || !rowNode) return null
      return this.getColumnIndexFromCell(rowNode, cellNode)
    },
    findNearestCellInfoInRow (quill, rowNode, index) {
      if (!quill || !rowNode) return null
      const cells = Array.from(rowNode.querySelectorAll('.faq-table__cell'))
      if (!cells.length) return null
      const enriched = cells
        .map(cell => this.getCellInfoFromNode(quill, cell))
        .filter(Boolean)
        .sort((a, b) => a.startIndex - b.startIndex)
      if (!enriched.length) return null
      const match = enriched.find(info => info.startIndex <= index && info.startIndex + info.blot.length() >= index)
      if (match) return match
      return enriched.reduce((closest, current) => {
        if (!closest) return current
        const closestDelta = Math.abs(index - closest.startIndex)
        const currentDelta = Math.abs(index - current.startIndex)
        return currentDelta < closestDelta ? current : closest
      }, null)
    },
    findFaqTableCellNode (domNode) {
      if (!domNode) return null
      if (domNode.classList && domNode.classList.contains('faq-table__cell')) {
        return domNode
      }
      return domNode.closest ? domNode.closest('.faq-table__cell') : null
    },
    findFaqTableRowNode (domNode) {
      if (!domNode) return null
      if (domNode.classList && domNode.classList.contains('faq-table__row')) {
        return domNode
      }
      return domNode.closest ? domNode.closest('.faq-table__row') : null
    },
    collectFaqTableRows (rowNode) {
      if (!rowNode || !rowNode.classList || !rowNode.classList.contains('faq-table__row')) {
        return []
      }
      const rows = [rowNode]
      let previous = rowNode.previousElementSibling
      while (previous && previous.classList && previous.classList.contains('faq-table__row')) {
        rows.unshift(previous)
        previous = previous.previousElementSibling
      }
      let next = rowNode.nextElementSibling
      while (next && next.classList && next.classList.contains('faq-table__row')) {
        rows.push(next)
        next = next.nextElementSibling
      }
      return rows
    },
    getCellTextLength (cell) {
      if (!cell) return 0
      const text = cell.textContent.replace(/\u00a0/g, '')
      return text.length
    },
    setCellPlaceholder (cell) {
      if (!cell) return
      cell.textContent = FAQ_TABLE_PLACEHOLDER
    },
    ensureFaqTableStructure (editorRoot) {
      const editor = editorRoot || (this.$el && this.$el.querySelector && this.$el.querySelector('.ql-editor'))
      if (!editor) return
      editor.querySelectorAll('.faq-table__row').forEach(rowEl => {
        const columns = this.getColumnsFromRow(rowEl)
        for (let columnIndex = 0; columnIndex < columns; columnIndex += 1) {
          const identifier = this.buildTableCellIdentifier(columnIndex)
          let cell = rowEl.querySelector(`.faq-table__cell[data-table-cell="${identifier}"]`)
          if (!cell) {
            cell = this.createFaqTableCell(identifier)
            const nextSibling = this.findNextCellSibling(rowEl, identifier)
            rowEl.insertBefore(cell, nextSibling)
          }
          this.ensureCellHasPlaceholder(cell)
        }
      })
    },
    reindexFaqTableColumns (rows, quillInstance) {
      if (!rows || !rows.length) return
      rows.forEach(row => {
        const cells = Array.from(row.querySelectorAll('.faq-table__cell')).sort((a, b) => {
          const aIndex = this.extractColumnNumber(a.getAttribute('data-table-cell'))
          const bIndex = this.extractColumnNumber(b.getAttribute('data-table-cell'))
          return aIndex - bIndex
        })
        cells.forEach((cell, index) => {
          const identifier = this.buildTableCellIdentifier(index)
          const blot = Quill.find(cell)
          if (blot) {
            blot.format('faq-table-cell', identifier)
          } else {
            cell.setAttribute('data-table-cell', identifier)
          }
          this.ensureCellHasPlaceholder(cell)
        })
        const newColumns = Math.max(Math.min(cells.length, MAX_TABLE_COLUMNS), 1)
        const position = row.getAttribute('data-table-position') || 'middle'
        const blot = Quill.find(row)
        if (blot) {
          blot.format('faq-table-row', { columns: newColumns, position })
        } else {
          row.setAttribute('data-table-columns', newColumns)
          this.applyRowPosition(row, position)
        }
      })
    },
    normalizeFaqTablePositions (editorRoot, quillInstance) {
      const editor = editorRoot || (this.$el && this.$el.querySelector && this.$el.querySelector('.ql-editor'))
      if (!editor) return
      const rows = Array.from(editor.querySelectorAll('.faq-table__row'))
      if (!rows.length) return
      let currentGroup = []
      rows.forEach(row => {
        const previousIsRow = row.previousElementSibling && row.previousElementSibling.classList && row.previousElementSibling.classList.contains('faq-table__row')
        if (!previousIsRow && currentGroup.length) {
          this.applyRowPositionsToGroup(currentGroup, quillInstance)
          currentGroup = []
        }
        currentGroup.push(row)
      })
      if (currentGroup.length) {
        this.applyRowPositionsToGroup(currentGroup, quillInstance)
      }
    },
    applyRowPositionsToGroup (rows, quillInstance) {
      const total = rows.length
      rows.forEach((row, index) => {
        let position = 'middle'
        if (total === 1) {
          position = 'single'
        } else if (index === 0) {
          position = 'first'
        } else if (index === total - 1) {
          position = 'last'
        }
        this.applyRowPosition(row, position)
        if (quillInstance) {
          const blot = Quill.find(row)
          if (blot) {
            blot.format('faq-table-row', {
              columns: this.getColumnsFromRow(row),
              position
            })
          }
        }
      })
    },
    applyRowPosition (rowEl, position) {
      if (!rowEl) return
      const allowed = ['first', 'middle', 'last', 'single']
      const normalized = allowed.includes(position) ? position : 'middle'
      rowEl.setAttribute('data-table-position', normalized)
      this.removeClassesByPrefix(rowEl, 'faq-table__row--pos-')
      rowEl.classList.add(`faq-table__row--pos-${normalized}`)
    },
    removeClassesByPrefix (node, prefix) {
      if (!node || !node.classList) return
      Array.from(node.classList)
        .filter(className => className.startsWith(prefix))
        .forEach(className => node.classList.remove(className))
    },
    logFaqTableSelection (quillInstance, range, contextLabel) {
      if (!quillInstance || !range) return
      try {
        const cellInfo = this.resolveFaqTableCellInfo(quillInstance, range.index)
        const rowInfo = this.resolveFaqTableRowInfo(quillInstance, range.index)
        const [line] = quillInstance.getLine(Math.max(range.index, 0))
        console.log('[FAQ table]', contextLabel, {
          selection: { index: range.index, length: range.length },
          cellFound: !!cellInfo,
          rowFound: !!rowInfo,
          cellStartIndex: cellInfo && cellInfo.startIndex,
          rowStartIndex: rowInfo && rowInfo.startIndex,
          rowColumns: rowInfo && this.getColumnsFromRow(rowInfo.node),
          rowPosition: rowInfo && rowInfo.node && rowInfo.node.getAttribute('data-table-position'),
          lineFormats: line && line.formats ? line.formats() : null
        })
        if (cellInfo && cellInfo.node) {
          console.log('[FAQ table] cell dataset', {
            dataset: { ...cellInfo.node.dataset },
            className: cellInfo.node.className
          })
        }
        if (rowInfo && rowInfo.node) {
          console.log('[FAQ table] row classes', rowInfo.node.className)
        }
      } catch (error) {
        console.warn('[FAQ table] failed to log selection', error)
      }
    },
    createFaqTableCell (identifier) {
      const cell = document.createElement('span')
      cell.classList.add('faq-table__cell')
      if (identifier) {
        cell.setAttribute('data-table-cell', identifier)
      }
      cell.textContent = FAQ_TABLE_PLACEHOLDER
      return cell
    },
    getColumnsFromRow (rowEl) {
      const dataAttr = parseInt(rowEl.getAttribute('data-table-columns'), 10)
      if (!Number.isNaN(dataAttr) && dataAttr > 0) return Math.min(dataAttr, MAX_TABLE_COLUMNS)
      const classEntry = Array.from(rowEl.classList).find(className => className.startsWith('faq-table__row--cols-'))
      if (classEntry) {
        const parsed = parseInt(classEntry.replace('faq-table__row--cols-', ''), 10)
        if (!Number.isNaN(parsed) && parsed > 0) {
          return Math.min(parsed, MAX_TABLE_COLUMNS)
        }
      }
      return 1
    },
    findNextCellSibling (rowEl, identifier) {
      const identifierIndex = this.extractColumnNumber(identifier)
      const cells = Array.from(rowEl.querySelectorAll('.faq-table__cell'))
      return cells.find(cell => {
        const cellIndex = this.extractColumnNumber(cell.getAttribute('data-table-cell'))
        return cellIndex > identifierIndex
      }) || null
    },
    ensureCellHasPlaceholder (cell) {
      if (!cell) return
      if (!cell.getAttribute('data-table-cell')) {
        const index = Array.from(cell.parentNode ? cell.parentNode.children : []).indexOf(cell)
        cell.setAttribute('data-table-cell', this.buildTableCellIdentifier(Math.max(index, 0)))
      }
      if (this.isCellEmpty(cell)) {
        cell.textContent = FAQ_TABLE_PLACEHOLDER
      }
    },
    isCellEmpty (cell) {
      if (!cell) return true
      const text = cell.textContent.replace(/\u00a0/g, '').trim()
      if (text.length > 0) return false
      const html = cell.innerHTML.replace(/&nbsp;/g, '').replace(/<br\s*\/?>/gi, '').trim()
      return html.length === 0
    },
    extractColumnNumber (identifier) {
      if (!identifier) return NaN
      const parsed = parseInt(identifier.replace('col-', ''), 10)
      return Number.isNaN(parsed) ? NaN : parsed
    },
    isTableActionBlockedInAccordion (quillInstance, range = null) {
      if (!quillInstance) return false
      const targetRange = range || quillInstance.getSelection(true)
      if (!targetRange) return false
      if (this.isSelectionInsideAccordionContent(quillInstance, targetRange.index)) {
        this.notifyFaqTableActionError(FAQ_TABLE_BLOCK_MESSAGE)
        return true
      }
      return false
    },
    isSelectionInsideAccordionContent (quillInstance, index) {
      if (!quillInstance || typeof index !== 'number') return false
      const safeIndex = Math.max(index, 0)
      const formats = quillInstance.getFormat(safeIndex)
      if (formats && formats['accordion-content']) return true
      const [leaf] = quillInstance.getLeaf(safeIndex)
      if (leaf && leaf.domNode && leaf.domNode.closest) {
        if (leaf.domNode.closest('.faq-accordion__content')) {
          return true
        }
      }
      return false
    }
  }
}
